import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Archive, ArchiveRestore, ClipboardList, Contact, FileSignature, Handshake, PiggyBank, Trash2, Wallet } from 'lucide-react';
import { Button, DataTable, KPICard, PageHeader, Tabs } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/axios';
import { type ContratSTDTO, deleteContratST, getSousTraitantsDashboard, listContratsST, updateContratST } from '@/services/finance';
import { AttachementsSTTab } from '@/modules/sous-traitants/AttachementsSTTab';
import { RepertoireTiersPage } from '@/modules/tiers/TiersPage';
import { toast } from '@/store/toastStore';
import { formatDZD, formatDZDCompact } from '@/utils/algeria';

export function SousTraitantsPage() {
  const [tab, setTab] = useState('annuaire');
  return (
    <>
      <PageHeader
        title="Sous-traitants"
        subtitle="Annuaire des sous-traitants et contrats de sous-traitance"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Sous-traitants' }]}
      />
      <Tabs
        tabs={[
          { key: 'annuaire', label: 'Annuaire', icon: <Contact className="size-4" /> },
          { key: 'contrats', label: 'Contrats', icon: <FileSignature className="size-4" /> },
          { key: 'attachements', label: 'Attachements', icon: <ClipboardList className="size-4" /> },
        ]}
        value={tab}
        onChange={setTab}
        className="mb-6"
      />
      {tab === 'annuaire' && <RepertoireTiersPage fixedType="SOUS_TRAITANT" embedded />}
      {tab === 'contrats' && <ContratsSTTab />}
      {tab === 'attachements' && <AttachementsSTTab />}
    </>
  );
}

function ContratsSTTab() {
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['contrats-st', { showArchived }], queryFn: () => listContratsST({ archives: showArchived }) });
  const { data: dash } = useQuery({ queryKey: ['st-dashboard'], queryFn: getSousTraitantsDashboard });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['contrats-st'] });
  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => updateContratST(id, { is_archived: archived }),
    onSuccess: (_d, v) => { invalidate(); toast.success(v.archived ? 'Contrat archivé.' : 'Contrat restauré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteContratST(id),
    onSuccess: () => { invalidate(); toast.success('Contrat supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const columns: ColumnDef<ContratSTDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Référence', size: 120 },
    { accessorKey: 'objet', header: 'Objet' },
    { accessorKey: 'montant_contrat', header: 'Montant', cell: ({ getValue }) => <span className="tabular-nums">{formatDZD(getValue() as number)}</span> },
    {
      accessorKey: 'avancement_pct',
      header: 'Avancement',
      size: 130,
      cell: ({ getValue }) => {
        const pct = Number(getValue());
        return (
          <div className="flex items-center gap-2">
            <div className="h-2 w-16 overflow-hidden rounded-full bg-surface-hover">
              <div className="h-full rounded-full bg-brand-primary" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs tabular-nums">{pct.toFixed(0)}%</span>
          </div>
        );
      },
    },
    { accessorKey: 'retenue_constituee', header: 'Retenue', cell: ({ getValue }) => <span className="tabular-nums">{formatDZD(getValue() as number)}</span> },
    {
      id: 'actions',
      header: '',
      size: 110,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          {showArchived ? (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: false })} aria-label="Restaurer"><ArchiveRestore className="size-4" /></Button>
          ) : (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: true })} aria-label="Archiver"><Archive className="size-4" /></Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm(`Supprimer le contrat « ${row.original.reference} » ?`)) deleteMutation.mutate(row.original.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard title="Exposition totale" value={formatDZDCompact(dash?.exposition_totale ?? 0)} icon={Handshake} accent="#1a1a2e" />
        <KPICard title="Règlements en attente" value={formatDZDCompact(dash?.reglements_en_attente ?? 0)} icon={Wallet} accent="#F97316" />
        <KPICard title="Retenues constituées" value={formatDZDCompact(dash?.retenues_constituees ?? 0)} icon={PiggyBank} accent="#00C875" />
        <KPICard title="Contrats actifs" value={String(dash?.nb_contrats_actifs ?? 0)} icon={Handshake} accent="#64748b" />
      </div>
      <div className="mb-3 flex justify-end">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
          <input type="checkbox" className="size-4 accent-brand-primary" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Afficher les archivés
        </label>
      </div>
      <DataTable columns={columns} data={data?.items ?? []} loading={isLoading} pageSize={15} exportFileName="contrats-sous-traitants" emptyMessage="Aucun contrat de sous-traitance." />
    </>
  );
}
